import { Alert, Box, Button, Group, Image, Text } from '@mantine/core';
import { IconCrop } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { barve } from '../tema';
import { izVlecenja, vPikslihSlike, type Pravokotnik } from './obrez';

/**
 * Izbor dela logotipa z miško ali prstom (kot obrez v UrejanjeLogotipa v Swingu); obreže strežnik. »Prekliči« pusti
 * logotip nespremenjen (v Swingu »Povrni«).
 */
export function ObrezSlike({
  src,
  obrezi,
  preklici,
  napaka,
  dela,
}: {
  src: string;
  obrezi: (p: Pravokotnik) => void;
  preklici: () => void;
  napaka?: string | null;
  dela: boolean;
}) {
  const slika = useRef<HTMLImageElement>(null);
  const [zacetek, setZacetek] = useState<{ x: number; y: number } | null>(null);
  const [izbor, setIzbor] = useState<Pravokotnik | null>(null);
  const [opozorilo, setOpozorilo] = useState<string | null>(null);

  const tocka = (e: React.PointerEvent) => {
    const r = slika.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const meja = () => ({ sirina: slika.current!.clientWidth, visina: slika.current!.clientHeight });

  const potrdi = () => {
    const img = slika.current;
    if (!img || !izbor || izbor.sirina < 3 || izbor.visina < 3) {
      setOpozorilo('Z miško (ali prstom) označite del slike, ki naj ostane.');
      return;
    }
    setOpozorilo(null);
    obrezi(vPikslihSlike(izbor, meja(), { sirina: img.naturalWidth, visina: img.naturalHeight }));
  };

  return (
    <Box>
      <Text size="sm" c="dimmed" mb="xs">
        Označite del logotipa, ki naj ostane, nato »Obreži na izbor«.
      </Text>
      <Box
        pos="relative"
        style={{ display: 'inline-block', touchAction: 'none', cursor: 'crosshair', userSelect: 'none' }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          const t = tocka(e);
          setZacetek(t);
          setIzbor(izVlecenja(t, t, meja()));
        }}
        onPointerMove={(e) => zacetek && setIzbor(izVlecenja(zacetek, tocka(e), meja()))}
        onPointerUp={() => setZacetek(null)}
        data-testid="obrez"
      >
        <Image ref={slika} src={src} alt="Logotip za obrez" mah={260} fit="contain" draggable={false} />
        {izbor && (
          <Box
            pos="absolute"
            style={{
              left: izbor.x,
              top: izbor.y,
              width: izbor.sirina,
              height: izbor.visina,
              border: `2px dashed ${barve.primarna}`,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.35)',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
      {(opozorilo || napaka) && (
        <Alert color="red" variant="light" mt="sm" role="alert">
          {opozorilo ?? napaka}
        </Alert>
      )}
      <Group mt="md" gap="xs">
        <Button leftSection={<IconCrop size={18} />} onClick={potrdi} loading={dela}>
          Obreži na izbor
        </Button>
        <Button variant="subtle" onClick={preklici} disabled={dela}>
          Prekliči
        </Button>
      </Group>
    </Box>
  );
}
